import React, { useMemo, useState } from 'react';

import { Button } from '@swyftx/aviary/atoms/Button';
import { Card } from '@swyftx/aviary/atoms/Card';
import { FlexLayout } from '@swyftx/aviary/atoms/Layout/Flex';
import { Separator } from '@swyftx/aviary/atoms/Seperator/Seperator';
import { Body, Heading } from '@swyftx/aviary/atoms/Typography';
import { useShopItems } from '@swyftx/features/shop/hooks';

import { FiatCodeEnum } from '@shared/enums';
import { formatCurrency } from '@shared/utils';

import { useMarkets } from 'src/lib/markets/hooks/useMarkets';

import { BuyViaLedgerButton } from '../BuyViaLedgerButton';
import { ShopPreviewImageCard } from '../ShopPreviewImageCard';

type Props = {
  itemId: string;
  initialSelectedVariant?: number;
  onBack: () => void;
};

const ShopItemDetailsCard: React.FC<Props> = ({ itemId, initialSelectedVariant = 0, onBack }) => {
  const { getItem } = useShopItems();
  const { getAssetByCode } = useMarkets();
  const [selectedVariant, setSelectedVariant] = useState<number>(initialSelectedVariant);

  const shopItem = useMemo(() => getItem(itemId), [getItem, itemId]);

  if (!shopItem) return null;

  return (
    <FlexLayout className='w-full'>
      <Card id={shopItem.id} className='p-24'>
        <FlexLayout direction='column' spacing={32}>
          <FlexLayout direction='row' spacing={16} className='flex justify-between'>
            <FlexLayout direction='column'>
              <Heading size='h4'>{shopItem.title}</Heading>
              <FlexLayout direction='row' spacing={4} className='flex items-center'>
                <Body size='large' color='primary'>
                  {formatCurrency(shopItem.price, getAssetByCode(FiatCodeEnum.AUD), {
                    appendCode: true,
                    hideCode: false,
                  })}
                </Body>
                <Body size='small' color='secondary'>
                  inc GST
                </Body>
              </FlexLayout>
            </FlexLayout>
            <FlexLayout direction='column'>
              <Button color='subtle' onClick={onBack} className='w-full'>
                Back
              </Button>
            </FlexLayout>
          </FlexLayout>
          <Separator />

          <FlexLayout direction='row' spacing={32} className='flex-wrap'>
            <div className='@xs:w-full @sm:w-[calc(50%-16px)]'>
              <ShopPreviewImageCard
                shopItem={shopItem}
                className='h-[450px]'
                selectedVariant={selectedVariant}
                setSelectedVariant={setSelectedVariant}
              />
            </div>

            <FlexLayout
              direction='column'
              spacing={16}
              className='@xs:w-full @sm:w-[calc(50%-16px)]'
              justifyContent='space-between'
            >
              <FlexLayout direction='column' spacing={16}>
                <FlexLayout direction='column' spacing={8}>
                  <Heading size='h6'>Product description</Heading>
                  <Body size='small'> {shopItem.description}</Body>
                </FlexLayout>

                <FlexLayout direction='column' spacing={8}>
                  <Heading size='h6'>Features</Heading>
                  <FlexLayout direction='column' spacing={4}>
                    {shopItem.features.map((feature, index) => (
                      <Body key={feature} size='small' id={`${shopItem.id}.feature.${index}`}>
                        {feature}
                      </Body>
                    ))}
                  </FlexLayout>
                </FlexLayout>

                <FlexLayout direction='column' spacing={8}>
                  <Heading size='h6'>Shipping</Heading>
                  <Body size='small'>
                    {shopItem.shipping.price
                      ? formatCurrency(shopItem.shipping.price, getAssetByCode(FiatCodeEnum.AUD), {
                          isApproximate: true,
                          hideCode: false,
                          appendCode: true,
                        })
                      : 'Free shipping'}{' '}
                    ({shopItem.shipping.minimumDays} - {shopItem.shipping.maximumDays} days)
                  </Body>
                </FlexLayout>
              </FlexLayout>
              <BuyViaLedgerButton shopItemVariant={shopItem.variants[selectedVariant]} />
            </FlexLayout>
          </FlexLayout>
        </FlexLayout>
      </Card>
    </FlexLayout>
  );
};

export { ShopItemDetailsCard };
